import classNames from 'classnames'
import { ASvg } from 'ui'
import { FormEvent } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useSiteInfo } from 'shared'
import SvgCheckTrueIcon from 'ui/svg/icons/stroke/check-true.svg'
import SvgCheckFalseIcon from 'ui/svg/icons/stroke/check-false.svg'
import { PrismicResultsBody } from '../../prismic-types-d'
import { MSearch } from '../molecules/m-search'
import { MResultContentItem } from '../molecules/m-result-content-item'
import { resultStyles } from './c-result-top'

interface CUsResultProps {
  quizResult: PrismicResultsBody | null
  handleSubmitEmail: (e: FormEvent<HTMLFormElement>) => void
  autopilotForm: string
  setAutopilotEmail: (param: string) => void
  autopilotEmail: string
  autopilotZipcode: string
  setAutopilotZipcode: (param: string) => void
  setIsConfirmed: (param: boolean) => void
  isConfirmed: boolean
  isClicked: boolean
}
export const CUsResult = ({
  quizResult,
  handleSubmitEmail,
  autopilotForm,
  setAutopilotEmail,
  autopilotEmail,
  setIsConfirmed,
  isConfirmed,
  isClicked,
  autopilotZipcode,
  setAutopilotZipcode,
}: CUsResultProps) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const siteInfo = useSiteInfo()

  const handleSubmit = (value: string) => {
    const query = {
      q: value,
    }
    const queryParams = new URLSearchParams(query).toString()
    router.push(`${siteInfo.doctorUrl}/?${queryParams}`, undefined, {
      shallow: true,
    })
  }
  return (
    <div className="w-full mt-10 md:px-24">
      <div className="quiz-congratulations bg-primary-teal text-neutral-white py-6 md:py-8 px-6 md:px-20">
        <h1 className="text-center text-2xl mb-5 md:mb-10">
          {quizResult?.primary.headline_before_result}{' '}
          <span className="font-bold">
            {quizResult?.primary.procedure_title}
          </span>
        </h1>
        <div className="quiz-nextstep md:flex md:items-center md:gap-[26px] md:mt-5">
          {siteInfo.isUS && (
            <h2 className="text-lg text-center md:text-left flex-1">
              {quizResult?.primary.find_a_doctor_heading}
            </h2>
          )}
          <div
            className={classNames('mt-5 md:mt-0 flex-1', {
              'mx-auto max-w-[500px]': siteInfo.isUK,
            })}
          >
            <MSearch onSubmit={handleSubmit} />
          </div>
        </div>
      </div>
      <div className="pt-8 px-6 lg:px-0">
        <h3 className="mb-3 text-2xl font-light">
          {quizResult?.primary.email_collection_header}
        </h3>
        <p className="mb-12">
          {quizResult?.primary.email_collection_description}
        </p>
        <form
          className="relative flex flex-col justify-between w-full mb-8 lg:flex-row flex-wrap gap-[10px] md:gap-4"
          onSubmit={(e) => handleSubmitEmail(e)}
        >
          {autopilotForm === 'success' && (
            <span
              className={classNames('form__msg--success', {
                'form__msg--show': autopilotForm === 'success',
              })}
            >
              Your report has been sent!
            </span>
          )}
          {autopilotForm === 'error' && (
            <span
              className={classNames('form__msg form__msg--error', {
                'form__msg--show': autopilotForm === 'error',
              })}
            >
              There was an error, please try again.
            </span>
          )}
          {autopilotForm !== 'success' && (
            <>
              <input
                type="email"
                placeholder={t('enter-your-email') ?? ''}
                required
                className="flex-1 px-5 py-3 text-lg bg-transparent bg-neutral-white border rounded-full placeholder-neutral-black text-charcoal"
                aria-label="Enter Your Email"
                onChange={(e) => setAutopilotEmail(e.target.value)}
                value={autopilotEmail}
              />
              {siteInfo.isUS && (
                <input
                  type="text"
                  placeholder={t('zip-code') ?? ''}
                  required
                  className="flex-1 px-5 py-3 text-lg bg-transparent bg-neutral-white border rounded-full placeholder-neutral-black text-charcoal"
                  aria-label="Enter Your Zip Code"
                  onChange={(e) => setAutopilotZipcode(e.target.value)}
                  value={autopilotZipcode}
                />
              )}
              <div
                className="mt-6 cursor-pointer flex w-full mb-2 md:order-3 md:w-full"
                onClick={() => setIsConfirmed(!isConfirmed)}
              >
                <ASvg
                  svg={isConfirmed ? SvgCheckTrueIcon : SvgCheckFalseIcon}
                  className={classNames(
                    'fill-current mr-4 text-primary-gray w-[12px] h-[12px]',
                    {
                      'text-secondary-red': !isConfirmed && isClicked,
                    }
                  )}
                />
                <div>
                  <p
                    className="text-sm text-neutral-dark-gray w-full mt-0 leading-tight [&>a]:underline [&>a]:text-primary-dark-teal"
                    dangerouslySetInnerHTML={{
                      __html: t('form-subscription-text') ?? '',
                    }}
                  />
                  {!isConfirmed && isClicked && (
                    <p className="text-xs font-bold text-secondary-red mt-1">
                      Checkbox is required.
                    </p>
                  )}
                </div>
              </div>
              <button
                className="px-8 py-4 mt-4 font-bold text-neutral-white uppercase duration-300 rounded-full md:text-sm md:py-1 md:mt-0 bg-primary-teal lg:hover:bg-primary-dark-teal flex-1 md:min-h-[54px]"
                aria-label="Submit"
                type="submit"
                disabled={autopilotForm === 'submitting'}
              >
                {autopilotForm === 'submitting'
                  ? `${t('submitting')}...`
                  : t('submit')}
              </button>
            </>
          )}
        </form>
        <style jsx>{resultStyles}</style>
      </div>
      {quizResult?.items.length && (
        <div>
          {quizResult.items.map((item, i) => {
            const isOdd = Boolean(i % 2 !== 0)
            return <MResultContentItem key={i} {...item} isOdd={isOdd} />
          })}
        </div>
      )}
    </div>
  )
}
